import React,{useState,useEffect,useCallback,useMemo} from 'react';
import {itemCommissionAPI,branchAPI,userAPI,productAPI} from '../utils/api';
import {PageHeader,SearchBar,Badge,Modal,FormInput,fmt,fmtDate,DataTable} from '../components/common';
import {
  DollarSign,TrendingUp,Users,Package,BarChart2,
  Plus,Edit,Trash2,CheckCircle,XCircle,RefreshCw,
  Download,Filter,ChevronDown,ChevronUp,
  Settings,List,FileText,Save,ToggleLeft,ToggleRight,
  AlertTriangle,Award,Building2
} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';
import {useLang} from '../i18n';

const today=()=>new Date().toISOString().split('T')[0];
const monthStart=()=>{const d=new Date();d.setDate(1);return d.toISOString().split('T')[0];};
const fmtN=n=>n!=null?parseFloat(n).toFixed(2):'0.00';

const STATUS_CFG={
  pending: {label:'Pending', color:'#d97706',bg:'#fef3c7'},
  approved:{label:'Approved',color:'#2563eb',bg:'#dbeafe'},
  paid:    {label:'Paid',    color:'#16a34a',bg:'#dcfce7'},
  cancelled:{label:'Cancelled',color:'#6b7280',bg:'#f3f4f6'},
};
const StatusPill=({status})=>{
  const c=STATUS_CFG[status]||{label:status,color:'#94a3b8',bg:'#f1f5f9'};
  return<span style={{padding:'2px 9px',borderRadius:20,fontSize:10,fontWeight:700,background:c.bg,color:c.color,whiteSpace:'nowrap'}}>{c.label}</span>;
};

// ── KPI Card ──────────────────────────────────────────────────────
const Kpi=({icon:Icon,label,value,color='var(--primary)',sub})=>(
  <div style={{background:'var(--surface)',borderRadius:12,padding:'14px 16px',border:'1px solid var(--border)'}}>
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
      <div style={{width:28,height:28,borderRadius:7,background:color+'20',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <Icon size={14} color={color}/>
      </div>
      <span style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5}}>{label}</span>
    </div>
    <div style={{fontSize:20,fontWeight:900,color}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{sub}</div>}
  </div>
);

// ════════════════════════════════════════════════════════════════
// TAB 1 — MASTER DATASET
// ════════════════════════════════════════════════════════════════
function MasterDatasetTab(){
  const[products,setProducts]=useState([]);
  const[ld,setLd]=useState(true);
  const[search,setSearch]=useState('');
  const[editing,setEditing]=useState({}); // product_id → form state
  const[saving,setSaving]=useState({});
  const[showConfigured,setShowConfigured]=useState(false);

  const load=useCallback(async()=>{
    setLd(true);
    try{const r=await itemCommissionAPI.listProducts({search,limit:500});setProducts(r.data);}
    catch{toast.error('Failed to load products');}
    finally{setLd(false);}
  },[search]);

  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t);},[load]);

  const cfg=p=>p.commissionConfig;

  const startEdit=p=>{
    const c=cfg(p);
    setEditing(e=>({...e,[p.id]:{
      commission_enabled:c?.commission_enabled||false,
      business_pct:String(c?.business_pct||0),
      branch_pct:String(c?.branch_pct||0),
      rep_pct:String(c?.rep_pct||0),
    }}));
  };

  const cancelEdit=pid=>setEditing(e=>{const n={...e};delete n[pid];return n;});

  const saveItem=async(p)=>{
    const form=editing[p.id];
    if(!form) return;
    setSaving(s=>({...s,[p.id]:true}));
    try{
      await itemCommissionAPI.upsert({
        product_id:p.id,
        commission_enabled:form.commission_enabled,
        business_pct:parseFloat(form.business_pct)||0,
        branch_pct:parseFloat(form.branch_pct)||0,
        rep_pct:parseFloat(form.rep_pct)||0,
      });
      toast.success(`Saved — ${p.name}`);
      cancelEdit(p.id);
      load();
    }catch{toast.error('Save failed');}
    finally{setSaving(s=>({...s,[p.id]:false}));}
  };

  const toggleEnabled=async(p)=>{
    const c=cfg(p);
    try{
      await itemCommissionAPI.upsert({
        product_id:p.id,
        commission_enabled:!(c?.commission_enabled),
        business_pct:parseFloat(c?.business_pct)||0,
        branch_pct:parseFloat(c?.branch_pct)||0,
        rep_pct:parseFloat(c?.rep_pct)||0,
      });
      toast.success('Updated');
      load();
    }catch{toast.error('Failed');}
  };

  const F=(pid,k,v)=>setEditing(e=>({...e,[pid]:{...e[pid],[k]:v}}));

  const displayed=showConfigured?products.filter(p=>cfg(p)):products;

  return(
    <div>
      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{flex:1,minWidth:200}}><SearchBar value={search} onChange={setSearch} placeholder="Search product name or SKU..."/></div>
        <button className={`btn btn-sm ${showConfigured?'btn-primary':'btn-ghost'}`}
          onClick={()=>setShowConfigured(v=>!v)} style={{gap:5,fontSize:12}}>
          <Filter size={13}/> {showConfigured?'Showing Configured':'Show All'}
        </button>
        <div style={{fontSize:12,color:'var(--td)'}}>
          {products.filter(p=>cfg(p)&&cfg(p).commission_enabled).length} products with commission enabled
        </div>
      </div>

      {ld&&<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>Loading...</div>}

      {!ld&&displayed.map(p=>{
        const c=cfg(p);
        const isEditing=!!editing[p.id];
        const form=editing[p.id]||{};
        const enabled=c?.commission_enabled||false;
        const isSaving=saving[p.id];

        // Live preview
        const sp=parseFloat(p.sale_price)||0;
        const cp=parseFloat(p.cost_price)||0;
        const np=sp-cp;
        const biz=np*(parseFloat(isEditing?form.business_pct:c?.business_pct||0)/100);
        const brn=np*(parseFloat(isEditing?form.branch_pct:c?.branch_pct||0)/100);
        const rep=np*(parseFloat(isEditing?form.rep_pct:c?.rep_pct||0)/100);

        return(
          <div key={p.id} style={{
            border:`1px solid ${enabled?'rgba(0,212,170,0.3)':'var(--border)'}`,
            borderLeft:`3px solid ${enabled?'var(--success)':'var(--border)'}`,
            borderRadius:10,marginBottom:8,overflow:'hidden',
            background:enabled?'rgba(0,212,170,0.02)':'var(--surface)',
          }}>
            {/* Header row */}
            <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',flexWrap:'wrap'}}>
              {/* Product info */}
              <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:200}}>
                {p.image_url&&<img src={p.image_url} alt="" style={{width:32,height:32,borderRadius:6,objectFit:'cover',border:'1px solid var(--border)'}}/>}
                <div>
                  <div style={{fontWeight:700,fontSize:13}}>{p.name}</div>
                  <div style={{fontSize:11,color:'var(--td)'}}>{p.sku} {p.category?.name?`· ${p.category.name}`:''}</div>
                </div>
              </div>

              {/* Prices */}
              <div style={{display:'flex',gap:14,fontSize:12}}>
                <span style={{color:'var(--td)'}}>Cost: <strong>{fmt(p.cost_price)}</strong></span>
                <span style={{color:'var(--td)'}}>Sale: <strong style={{color:'var(--success)'}}>{fmt(p.sale_price)}</strong></span>
                <span style={{color:'var(--td)'}}>Net/unit: <strong style={{color:np>0?'var(--primary)':'var(--danger)'}}>{fmt(np)}</strong></span>
              </div>

              {/* Commission toggle */}
              <button className="btn btn-ghost btn-sm" onClick={()=>toggleEnabled(p)}
                style={{gap:5,color:enabled?'var(--success)':'var(--td)',fontSize:11}}>
                {enabled?<ToggleRight size={16} color="var(--success)"/>:<ToggleLeft size={16}/>}
                {enabled?'Commission ON':'Commission OFF'}
              </button>

              {/* Edit / Save */}
              {!isEditing?(
                <button className="btn btn-ghost btn-sm" onClick={()=>startEdit(p)}>
                  <Edit size={13}/> Configure
                </button>
              ):(
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-primary btn-sm" onClick={()=>saveItem(p)} disabled={isSaving}>
                    {isSaving?<RefreshCw size={12} style={{animation:'spin 1s linear infinite'}}/>:<Save size={12}/>} Save
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>cancelEdit(p.id)}>Cancel</button>
                </div>
              )}
            </div>

            {/* Config row (always visible if configured, or if editing) */}
            {(c||isEditing)&&(
              <div style={{padding:'8px 14px 12px',borderTop:'1px solid var(--border)',background:'var(--s2)'}}>
                {isEditing?(
                  <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                      <input type="checkbox" checked={!!form.commission_enabled}
                        onChange={e=>F(p.id,'commission_enabled',e.target.checked)}
                        style={{accentColor:'var(--primary)',width:14,height:14}}/>
                      Commission Enabled
                    </label>
                    {[['Business %','business_pct','var(--accent)'],['Branch %','branch_pct','var(--primary)'],['Sales Rep %','rep_pct','var(--success)']].map(([label,key,color])=>(
                      <div key={key} style={{display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontSize:11,color,fontWeight:700}}>{label}</span>
                        <input type="number" min={0} max={100} step={0.1} className="form-input"
                          value={form[key]||''}
                          onChange={e=>F(p.id,key,e.target.value)}
                          style={{width:70,textAlign:'right',fontSize:13}}/>
                      </div>
                    ))}
                    {/* Live preview */}
                    {np>0&&(
                      <div style={{marginLeft:'auto',display:'flex',gap:10,fontSize:11,color:'var(--td)'}}>
                        <span>Biz: <strong style={{color:'var(--accent)'}}>{fmt(biz)}</strong></span>
                        <span>Branch: <strong style={{color:'var(--primary)'}}>{fmt(brn)}</strong></span>
                        <span>Rep: <strong style={{color:'var(--success)'}}>{fmt(rep)}</strong></span>
                      </div>
                    )}
                  </div>
                ):(
                  <div style={{display:'flex',gap:18,flexWrap:'wrap',fontSize:12}}>
                    <span style={{color:'var(--td)'}}>
                      Commission: <Badge variant={enabled?'success':'danger'}>{enabled?'YES':'NO'}</Badge>
                    </span>
                    {[['Business',c?.business_pct,'var(--accent)'],['Branch',c?.branch_pct,'var(--primary)'],['Sales Rep',c?.rep_pct,'var(--success)']].map(([label,pct,color])=>(
                      <span key={label} style={{color:'var(--td)'}}>
                        {label}: <strong style={{color}}>{pct||0}%</strong>
                        {np>0&&<span style={{color:'var(--td)',marginLeft:4}}>({fmt(np*(parseFloat(pct||0)/100))})</span>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TAB 2 — SALES TABLE
// ════════════════════════════════════════════════════════════════
function SalesTableTab({canManage=true,viewOwnOnly=false}){
  const{myBranchId,isOwner,hasPermission}=useAuth();
  const canSeeCost=hasPermission('cost_price.view');
  const[sales,setSales]=useState([]);
  const[ld,setLd]=useState(false);
  const[dateFrom,setDateFrom]=useState(monthStart());
  const[dateTo,setDateTo]=useState(today());
  const[branchId,setBranchId]=useState('');
  const[repId,setRepId]=useState('');
  const[branches,setBranches]=useState([]);
  const[reps,setReps]=useState([]);
  const[products,setProducts]=useState([]);
  const[modal,setModal]=useState(null);
  const[form,setForm]=useState({});
  const[delId,setDelId]=useState(null);

  useEffect(()=>{
    Promise.all([
      branchAPI.getAll({limit:100}),
      userAPI.getAll({limit:200}),
      itemCommissionAPI.listProducts({limit:500}),
    ]).then(([b,u,p])=>{
      setBranches(b.data.data||b.data);
      setReps((u.data.data||u.data).filter(u=>u.is_active));
      setProducts(p.data||[]);
    }).catch(()=>{});
  },[]);

  const load=useCallback(async()=>{
    setLd(true);
    try{
      const p={date_from:dateFrom,date_to:dateTo};
      if(branchId) p.branch_id=branchId;
      if(repId) p.sales_rep_id=repId;
      const r=await itemCommissionAPI.listSales(p);
      setSales(r.data||[]);
    }catch{toast.error('Failed');}
    finally{setLd(false);}
  },[dateFrom,dateTo,branchId,repId]);

  useEffect(()=>{load();},[]);

  const blankForm={sale_date:today(),product_id:'',branch_id:String(myBranchId||''),sales_rep_id:'',quantity:1,sale_price:'',cost_price:'',notes:''};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  // Auto-fill sale/cost price when product selected
  const handleProductChange=v=>{
    F('product_id',v);
    const p=products.find(x=>String(x.id)===String(v));
    if(p){
      F('sale_price',String(parseFloat(p.sale_price)||0));
      F('cost_price',String(parseFloat(p.cost_price)||0));
    }
  };

  // Live profit preview
  const qty=parseFloat(form.quantity)||1;
  const sp=parseFloat(form.sale_price)||0;
  const cp=parseFloat(form.cost_price)||0;
  const np=(qty*(sp-cp));
  const selProduct=products.find(x=>String(x.id)===String(form.product_id));
  const selCfg=selProduct?.commissionConfig;
  const bizAmt=selCfg?np*(parseFloat(selCfg.business_pct||0)/100):0;
  const brnAmt=selCfg?np*(parseFloat(selCfg.branch_pct||0)/100):0;
  const repAmt=selCfg?.commission_enabled?np*(parseFloat(selCfg.rep_pct||0)/100):0;

  const save=async()=>{
    if(!form.product_id||!form.sale_price) return toast.error('Product and sale price required');
    try{
      const payload={...form,product_id:parseInt(form.product_id),branch_id:parseInt(form.branch_id)||null,sales_rep_id:parseInt(form.sales_rep_id)||null,quantity:parseFloat(form.quantity)||1,sale_price:parseFloat(form.sale_price),cost_price:parseFloat(form.cost_price)||0};
      if(modal==='new') await itemCommissionAPI.createSale(payload);
      else await itemCommissionAPI.updateSale(form.id,payload);
      toast.success(modal==='new'?'Sale recorded':'Updated');
      setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  const del=async()=>{
    try{await itemCommissionAPI.deleteSale(delId);toast.success('Deleted');setDelId(null);load();}
    catch{toast.error('Failed');}
  };

  const setStatus=async(id,status)=>{
    try{await itemCommissionAPI.updateStatus(id,status);toast.success(`→ ${status}`);load();}
    catch{toast.error('Failed');}
  };

  const exportCSV=()=>{
    const headers=['Date','Product','SKU','Branch','Sales Rep','Qty','Sale Price','Cost','Net Profit','Biz $','Branch $','Rep $','Commission','Status'].join(',');
    const rows=sales.map(s=>[
      s.sale_date,`"${s.product?.name||''}"`,s.product?.sku||'',
      `"${s.branch?.name||''}"`,
      s.salesRep?`"${s.salesRep.first_name} ${s.salesRep.last_name}"`:'—',
      s.quantity,fmtN(s.sale_price),fmtN(s.cost_price),fmtN(s.net_profit),
      fmtN(s.business_amount),fmtN(s.branch_amount),fmtN(s.rep_amount),
      s.commission_enabled?'YES':'NO',s.status,
    ].join(','));
    const blob=new Blob([[headers,...rows].join('\n')],{type:'text/csv'});
    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`commission-sales-${today()}.csv`;a.click();
  };

  const productOpts=[{value:'',label:'— Select Product —'},...products.map(p=>({value:String(p.id),label:`${p.name} (${p.sku})`}))];
  const branchOpts=[{value:'',label:'— All Branches —'},...branches.map(b=>({value:String(b.id),label:b.name}))];
  const repOpts=[{value:'',label:'— All Reps —'},...reps.map(u=>({value:String(u.id),label:`${u.first_name} ${u.last_name}`}))];
  const repOptsForm=[{value:'',label:'— Unassigned —'},...reps.map(u=>({value:String(u.id),label:`${u.first_name} ${u.last_name}`}))];

  // Totals
  const totals=useMemo(()=>sales.filter(s=>s.status!=='cancelled').reduce((acc,s)=>({
    net:acc.net+(parseFloat(s.net_profit)||0),
    biz:acc.biz+(parseFloat(s.business_amount)||0),
    brn:acc.brn+(parseFloat(s.branch_amount)||0),
    rep:acc.rep+(parseFloat(s.rep_amount)||0),
  }),{net:0,biz:0,brn:0,rep:0}),[sales]);

  const selectStyle={padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12};

  return(
    <div>
      {/* View-only scope banner */}
      {viewOwnOnly&&(
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.25)',borderRadius:8,marginBottom:12,fontSize:12,color:'var(--primary)'}}>
          <Users size={14}/> <strong>Showing your commission records only.</strong> You can view your earnings but cannot add or edit records.
        </div>
      )}
      {/* Filter bar */}
      <div style={{background:'var(--surface)',borderRadius:12,padding:'12px 16px',border:'1px solid var(--border)',marginBottom:14,display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={selectStyle}/>
        <span style={{color:'var(--td)',fontSize:12}}>to</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={selectStyle}/>
        {/* Branch filter: hidden for view_own users (they always see only their branch) */}
        {!viewOwnOnly&&(
          <select value={branchId} onChange={e=>setBranchId(e.target.value)} style={selectStyle}>
            {branchOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        {/* Rep filter: hidden for view_own users (they always see only themselves) */}
        {!viewOwnOnly&&(
          <select value={repId} onChange={e=>setRepId(e.target.value)} style={selectStyle}>
            {repOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        <button className="btn btn-primary btn-sm" onClick={load} disabled={ld} style={{gap:5}}>
          {ld?<RefreshCw size={12} style={{animation:'spin 1s linear infinite'}}/>:<Filter size={12}/>} Run
        </button>
        <button className="btn btn-ghost btn-sm" onClick={exportCSV} style={{gap:5}}><Download size={12}/> CSV</button>
        {canManage&&(
          <button className="btn btn-primary btn-sm" style={{marginLeft:'auto',gap:5}} onClick={()=>{setForm({...blankForm});setModal('new');}}>
          <Plus size={13}/> Add Sale
        </button>
        )}
      </div>

      {/* KPIs */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:14}}>
        <Kpi icon={FileText} label="Records" value={sales.length} color="var(--primary)"/>
        <Kpi icon={TrendingUp} label="Net Profit" value={fmt(totals.net)} color="var(--success)"/>
        <Kpi icon={Building2} label="Business $" value={fmt(totals.biz)} color="var(--accent)"/>
        <Kpi icon={Building2} label="Branch $" value={fmt(totals.brn)} color="var(--primary)"/>
        <Kpi icon={Users} label="Rep Commissions" value={fmt(totals.rep)} color="var(--warning)"/>
      </div>

      {/* Table */}
      <div style={{overflowX:'auto',border:'1px solid var(--border)',borderRadius:10}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'var(--s2)'}}>
              {['Date','Product','Branch','Sales Rep','Qty','Sale Price',...(canSeeCost?['Cost','Net Profit']:[]),'Biz $','Branch $','Rep $','Commission','Status',''].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:['Qty','Sale Price','Cost','Net Profit','Biz $','Branch $','Rep $'].includes(h)?'right':'left',fontSize:10,fontWeight:700,color:'var(--td)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ld&&<tr><td colSpan={14} style={{textAlign:'center',padding:30,color:'var(--td)'}}>Loading...</td></tr>}
            {!ld&&!sales.length&&<tr><td colSpan={14} style={{textAlign:'center',padding:30,color:'var(--td)'}}>No records — add a sale above</td></tr>}
            {!ld&&sales.map((s,i)=>(
              <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:s.status==='cancelled'?'rgba(100,116,139,0.05)':i%2===0?'var(--surface)':'var(--s2)',opacity:s.status==='cancelled'?0.6:1}}>
                <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>{fmtDate(s.sale_date)}</td>
                <td style={{padding:'8px 10px'}}>
                  <div style={{fontWeight:600}}>{s.product?.name||'—'}</div>
                  <div style={{fontSize:10,color:'var(--td)',fontFamily:'monospace'}}>{s.product?.sku||''}</div>
                </td>
                <td style={{padding:'8px 10px',color:'var(--td)'}}>{s.branch?.name||'—'}</td>
                <td style={{padding:'8px 10px',color:'var(--td)'}}>{s.salesRep?`${s.salesRep.first_name} ${s.salesRep.last_name}`:'—'}</td>
                <td style={{padding:'8px 10px',textAlign:'right'}}>{s.quantity}</td>
                <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700}}>{fmt(s.sale_price)}</td>
                {canSeeCost&&<td style={{padding:'8px 10px',textAlign:'right',color:'var(--td)'}}>{fmt(s.cost_price)}</td>}
                {canSeeCost&&<td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,color:parseFloat(s.net_profit)>0?'var(--success)':'var(--danger)'}}>{fmt(s.net_profit)}</td>}
                <td style={{padding:'8px 10px',textAlign:'right',color:'var(--accent)',fontWeight:600}}>{fmt(s.business_amount)}</td>
                <td style={{padding:'8px 10px',textAlign:'right',color:'var(--primary)',fontWeight:600}}>{fmt(s.branch_amount)}</td>
                <td style={{padding:'8px 10px',textAlign:'right',color:'var(--warning)',fontWeight:700}}>{fmt(s.rep_amount)}</td>
                <td style={{padding:'8px 10px'}}>
                  <Badge variant={s.commission_enabled?'success':'danger'}>{s.commission_enabled?'YES':'NO'}</Badge>
                </td>
                <td style={{padding:'8px 10px'}}><StatusPill status={s.status}/></td>
                <td style={{padding:'8px 10px'}}>
                  <div style={{display:'flex',gap:4}}>
                    {canManage&&s.status==='pending'&&<button className="btn btn-ghost btn-sm" onClick={()=>setStatus(s.id,'approved')} title="Approve" style={{color:'var(--success)',padding:4}}><CheckCircle size={13}/></button>}
                    {canManage&&s.status==='approved'&&<button className="btn btn-ghost btn-sm" onClick={()=>setStatus(s.id,'paid')} title="Mark Paid" style={{color:'var(--primary)',padding:4}}><Award size={13}/></button>}
                    {canManage&&!['paid','cancelled'].includes(s.status)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...s,product_id:String(s.product_id),branch_id:String(s.branch_id||''),sales_rep_id:String(s.sales_rep_id||''),sale_price:String(s.sale_price),cost_price:String(s.cost_price),quantity:String(s.quantity)});setModal('edit');}} title="Edit" style={{padding:4}}><Edit size={13}/></button>}
                    {canManage&&s.status!=='cancelled'&&<button className="btn btn-ghost btn-sm" onClick={()=>setStatus(s.id,'cancelled')} title="Cancel" style={{color:'var(--danger)',padding:4}}><XCircle size={13}/></button>}
                    {canManage&&<button className="btn btn-danger btn-sm" onClick={()=>setDelId(s.id)} style={{padding:4}}><Trash2 size={13}/></button>}
                    {!canManage&&<span style={{fontSize:10,color:'var(--td)'}}>View only</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?'Record Commission Sale':'Edit Sale'} width={600}>
        <div className="form-grid">
          <FormInput label="Sale Date" value={form.sale_date||today()} onChange={v=>F('sale_date',v)} type="date"/>
          <FormInput label="Product *" value={String(form.product_id||'')} onChange={handleProductChange} options={productOpts}/>
          <FormInput label="Branch" value={String(form.branch_id||'')} onChange={v=>F('branch_id',v)} options={branchOpts}/>
          <FormInput label="Sales Rep" value={String(form.sales_rep_id||'')} onChange={v=>F('sales_rep_id',v)} options={repOptsForm}/>
          <FormInput label="Quantity" value={String(form.quantity||1)} onChange={v=>F('quantity',v)} type="number"/>
          <FormInput label="Sale Price ($)" value={String(form.sale_price||'')} onChange={v=>F('sale_price',v)} type="number"/>
          <FormInput label="Cost Price ($)" value={String(form.cost_price||'')} onChange={v=>F('cost_price',v)} type="number"/>
        </div>

        {/* Live profit preview */}
        {form.product_id&&(
          <div style={{margin:'10px 0',padding:14,background:'var(--s2)',borderRadius:10,border:'1px solid var(--border)'}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:10,letterSpacing:.4}}>PROFIT SPLIT PREVIEW</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,textAlign:'center'}}>
              {[
                ['Net Profit',fmt(np),parseFloat(np)>0?'var(--success)':'var(--danger)'],
                ['Business',fmt(bizAmt),'var(--accent)'],
                ['Branch',fmt(brnAmt),'var(--primary)'],
                ['Sales Rep',selCfg?.commission_enabled?fmt(repAmt):'—',selCfg?.commission_enabled?'var(--warning)':'var(--td)'],
              ].map(([label,val,color])=>(
                <div key={label} style={{padding:'8px',background:'var(--surface)',borderRadius:8,border:'1px solid var(--border)'}}>
                  <div style={{fontSize:10,color:'var(--td)',fontWeight:700,marginBottom:4}}>{label}</div>
                  <div style={{fontSize:15,fontWeight:800,color}}>{val}</div>
                </div>
              ))}
            </div>
            {selCfg&&(
              <div style={{marginTop:8,fontSize:11,color:'var(--td)',textAlign:'center'}}>
                Commission: <Badge variant={selCfg.commission_enabled?'success':'danger'}>{selCfg.commission_enabled?'Enabled':'Disabled'}</Badge>
                {selCfg.commission_enabled&&<span style={{marginLeft:8}}>
                  Biz {selCfg.business_pct}% · Branch {selCfg.branch_pct}% · Rep {selCfg.rep_pct}%
                </span>}
              </div>
            )}
            {!selCfg&&<div style={{marginTop:6,fontSize:11,color:'var(--warning)',textAlign:'center'}}>⚠ No commission config for this product — configure in Master Dataset tab</div>}
          </div>
        )}

        <FormInput label="Notes" value={form.notes||''} onChange={v=>F('notes',v)} type="textarea"/>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save</button>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Sale Record" width={400}>
        <p className="text-muted" style={{marginBottom:18}}>Delete this commission sale record permanently?</p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setDelId(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={del}>Delete</button>
        </div>
      </Modal>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TAB 3 — REPORTS
// ════════════════════════════════════════════════════════════════
function CommissionReportsTab({canManageAll=true,viewOwnOnly=false}){
  const{hasPermission}=useAuth();
  const canSeeCost=hasPermission('cost_price.view');
  const[data,setData]=useState(null);
  const[ld,setLd]=useState(false);
  const[dateFrom,setDateFrom]=useState(monthStart());
  const[dateTo,setDateTo]=useState(today());
  const[branches,setBranches]=useState([]);
  const[reps,setReps]=useState([]);
  const[branchId,setBranchId]=useState('');
  const[channelFilter,setChannelFilter]=useState('');
  const[view,setView]=useState('rep'); // rep|product|day|month|channel|unassigned
  const[assigningRep,setAssigningRep]=useState(null); // {id,reference_type,reference_id}
  const[assignRepId,setAssignRepId]=useState('');

  useEffect(()=>{
    Promise.all([
      branchAPI.getAll({limit:100}),
      userAPI.getAll({limit:200}),
    ]).then(([b,u])=>{
      setBranches(b.data.data||b.data);
      setReps((u.data.data||u.data).filter(x=>x.is_active));
    }).catch(()=>{});
  },[]);

  const load=useCallback(async()=>{
    setLd(true);
    try{
      const p={date_from:dateFrom,date_to:dateTo};
      if(branchId) p.branch_id=branchId;
      if(channelFilter) p.reference_type=channelFilter;
      const r=await itemCommissionAPI.getReport(p);
      setData(r.data);
    }catch{toast.error('Failed');}
    finally{setLd(false);}
  },[dateFrom,dateTo,branchId,channelFilter]);

  useEffect(()=>{load();},[]);

  const doAssignRep=async(row)=>{
    if(!assignRepId) return toast.error('Select a sales rep');
    try{
      await itemCommissionAPI.assignRep({
        reference_type:row.reference_type,
        reference_id:row.reference_id,
        sales_rep_id:parseInt(assignRepId),
      });
      toast.success('Sales rep assigned');
      setAssigningRep(null);setAssignRepId('');
      load();
    }catch{toast.error('Failed');}
  };

  const selectStyle={padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12};

  const TH=({children,right})=><th style={{padding:'8px 10px',textAlign:right?'right':'left',fontSize:10,fontWeight:700,color:'var(--td)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{children}</th>;
  const TD=({children,right,bold,color})=><td style={{padding:'8px 10px',textAlign:right?'right':'left',fontWeight:bold?700:400,color:color||'var(--text)'}}>{children}</td>;

  return(
    <div>
      {/* Scope banner for view_own users */}
      {viewOwnOnly&&(
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.25)',borderRadius:8,marginBottom:12,fontSize:12,color:'var(--primary)'}}>
          <Users size={14}/> <strong>Viewing your own commissions only.</strong> Contact your manager to see branch-wide reports.
        </div>
      )}
      {/* Controls */}
      <div style={{background:'var(--surface)',borderRadius:12,padding:'12px 16px',border:'1px solid var(--border)',marginBottom:14,display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={selectStyle}/>
        <span style={{fontSize:12,color:'var(--td)'}}>to</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={selectStyle}/>
        {/* Branch filter only for managers/owners */}
        {canManageAll&&(
          <select value={branchId} onChange={e=>setBranchId(e.target.value)} style={selectStyle}>
            <option value="">All Branches</option>
            {branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={channelFilter} onChange={e=>setChannelFilter(e.target.value)} style={selectStyle}>
          <option value="">All Channels</option>
          {[['invoice','Invoice'],['pos_transaction','POS'],['sale_order','Sale Order'],['manual','Manual'],['emenu','E-Menu']].map(([v,l])=>(
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <button className="btn btn-primary btn-sm" onClick={load} disabled={ld} style={{gap:5}}>
          {ld?<RefreshCw size={12} style={{animation:'spin 1s linear infinite'}}/>:<Filter size={12}/>} Run
        </button>
      </div>

      {data&&<>
        {/* Grand totals */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:16}}>
          <Kpi icon={FileText} label="Total Sales" value={data.totals.count} color="var(--primary)"/>
          {canSeeCost&&<Kpi icon={TrendingUp} label="Total Net Profit" value={`$${fmtN(data.totals.net_profit)}`} color="var(--success)"/>}
          <Kpi icon={Building2} label="Business $" value={`$${fmtN(data.totals.business_amount)}`} color="var(--accent)"/>
          <Kpi icon={Building2} label="Branch $" value={`$${fmtN(data.totals.branch_amount)}`} color="var(--primary)"/>
          <Kpi icon={Users} label="Rep Commissions" value={`$${fmtN(data.totals.rep_amount)}`} color="var(--warning)"/>
          {(data.unassigned_count||0)>0&&(
            <div onClick={()=>setView('unassigned')} style={{cursor:'pointer'}}>
              <Kpi icon={AlertTriangle} label="Unassigned Rep" value={data.unassigned_count} color="var(--danger)" sub="Click to assign"/>
            </div>
          )}
        </div>

        {/* View toggle */}
        <div style={{display:'flex',gap:4,marginBottom:14,borderBottom:'1px solid var(--border)',flexWrap:'wrap'}}>
          {[
            ['rep','By Sales Rep',Users],
            ['product','By Product',Package],
            ['day','By Day',BarChart2],
            ['month','By Month',FileText],
            ['channel','By Channel',List],
            ...(data.unassigned_count>0?[['unassigned',`Unassigned (${data.unassigned_count})`,AlertTriangle]]:[]),
          ].map(([k,label,Icon])=>(
            <button key={k} onClick={()=>setView(k)}
              style={{display:'flex',alignItems:'center',gap:5,padding:'8px 14px',fontSize:12,fontWeight:600,
                border:'none',borderBottom:`2px solid ${view===k?'var(--primary)':'transparent'}`,
                background:'transparent',cursor:'pointer',
                color:view===k?'var(--primary)':k==='unassigned'?'var(--danger)':'var(--td)',marginBottom:-1}}>
              <Icon size={13}/>{label}
            </button>
          ))}
        </div>

        {/* By Rep */}
        {view==='rep'&&(
          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'var(--s2)'}}>
                <TH>Sales Rep</TH><TH right>Sales</TH>
                {canSeeCost&&<TH right>Net Profit</TH>}
                <TH right>Rep Commission</TH>
              </tr></thead>
              <tbody>
                {data.by_rep.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                    <TD bold>{r.name}</TD>
                    <TD right>{r.sales}</TD>
                    {canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}
                    <TD right bold color="var(--warning)">{fmt(r.rep_amount)}</TD>
                  </tr>
                ))}
                {!data.by_rep.length&&<tr><td colSpan={4} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* By Product */}
        {view==='product'&&(
          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'var(--s2)'}}>
                <TH>Product</TH><TH>SKU</TH><TH right>Qty Sold</TH>
                {canSeeCost&&<TH right>Net Profit</TH>}
                <TH right>Biz $</TH><TH right>Branch $</TH><TH right>Rep $</TH>
              </tr></thead>
              <tbody>
                {data.by_product.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                    <TD bold>{r.name}</TD>
                    <TD><span style={{fontFamily:'monospace',fontSize:11,color:'var(--primary)'}}>{r.sku}</span></TD>
                    <TD right>{r.qty}</TD>
                    {canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}
                    <TD right color="var(--accent)">{fmt(r.business_amount)}</TD>
                    <TD right color="var(--primary)">{fmt(r.branch_amount)}</TD>
                    <TD right color="var(--warning)">{fmt(r.rep_amount)}</TD>
                  </tr>
                ))}
                {!data.by_product.length&&<tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* By Day */}
        {view==='day'&&(
          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'var(--s2)'}}>
                <TH>Date</TH><TH right>Sales</TH>
                {canSeeCost&&<TH right>Net Profit</TH>}
                <TH right>Biz $</TH><TH right>Branch $</TH><TH right>Rep $</TH>
              </tr></thead>
              <tbody>
                {data.by_day.map((r,i)=>(
                  <tr key={r.date} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                    <TD bold>{fmtDate(r.date)}</TD>
                    <TD right>{r.sales}</TD>
                    {canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}
                    <TD right color="var(--accent)">{fmt(r.business_amount)}</TD>
                    <TD right color="var(--primary)">{fmt(r.branch_amount)}</TD>
                    <TD right color="var(--warning)">{fmt(r.rep_amount)}</TD>
                  </tr>
                ))}
                {!data.by_day.length&&<tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* By Month */}
        {view==='month'&&(
          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'var(--s2)'}}>
                <TH>Month</TH><TH right>Sales</TH>
                {canSeeCost&&<TH right>Net Profit</TH>}
                <TH right>Biz $</TH><TH right>Branch $</TH><TH right>Rep $</TH>
              </tr></thead>
              <tbody>
                {(data.by_month||[]).map((r,i)=>(
                  <tr key={r.month} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                    <TD bold>{r.month}</TD>
                    <TD right>{r.sales}</TD>
                    {canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}
                    <TD right color="var(--accent)">{fmt(r.business_amount)}</TD>
                    <TD right color="var(--primary)">{fmt(r.branch_amount)}</TD>
                    <TD right color="var(--warning)">{fmt(r.rep_amount)}</TD>
                  </tr>
                ))}
                {!(data.by_month||[]).length&&<tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* By Channel */}
        {view==='channel'&&(
          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'var(--s2)'}}>
                <TH>Channel</TH><TH right>Sales</TH>
                {canSeeCost&&<TH right>Net Profit</TH>}
                <TH right>Biz $</TH><TH right>Branch $</TH><TH right>Rep $</TH>
              </tr></thead>
              <tbody>
                {(data.by_channel||[]).map((r,i)=>(
                  <tr key={r.channel} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                    <TD bold>{r.label||r.channel}</TD>
                    <TD right>{r.sales}</TD>
                    {canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}
                    <TD right color="var(--accent)">{fmt(r.business_amount)}</TD>
                    <TD right color="var(--primary)">{fmt(r.branch_amount)}</TD>
                    <TD right color="var(--warning)">{fmt(r.rep_amount)}</TD>
                  </tr>
                ))}
                {!(data.by_channel||[]).length&&<tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Unassigned Rep — assign sales rep to e-menu / unassigned sales */}
        {view==='unassigned'&&(
          <div>
            <div style={{padding:'10px 14px',background:'rgba(255,68,68,0.08)',border:'1px solid rgba(255,68,68,0.2)',borderRadius:8,marginBottom:12,fontSize:12,color:'var(--danger)'}}>
              <strong>⚠ {data.unassigned_count} commission record(s)</strong> have no sales rep assigned.
              These are typically from e-menu orders or POS sales with no rep selected.
              Assign a rep below to credit the correct person.
            </div>
            <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'var(--s2)'}}>
                  <TH>Date</TH><TH>Product</TH><TH>Channel</TH>
                  {canSeeCost&&<TH right>Net Profit</TH>}
                  <TH right>Rep $</TH><TH>Assign Rep</TH>
                </tr></thead>
                <tbody>
                  {(data.unassigned||[]).map((r,i)=>(
                    <tr key={r.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                      <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>{fmtDate(r.sale_date)}</td>
                      <td style={{padding:'7px 10px',fontWeight:600}}>{r.product}</td>
                      <td style={{padding:'7px 10px'}}>
                        <span style={{fontSize:10,padding:'2px 7px',borderRadius:10,background:'var(--pm)',color:'var(--primary)',fontWeight:700}}>{r.channel}</span>
                      </td>
                      {canSeeCost&&<td style={{padding:'7px 10px',textAlign:'right',color:'var(--success)',fontWeight:700}}>{fmt(r.net_profit)}</td>}
                      <td style={{padding:'7px 10px',textAlign:'right',color:'var(--warning)',fontWeight:700}}>{fmt(r.rep_amount)}</td>
                      <td style={{padding:'7px 10px'}}>
                        {assigningRep===r.id?(
                          <div style={{display:'flex',gap:6,alignItems:'center'}}>
                            <select value={assignRepId} onChange={e=>setAssignRepId(e.target.value)}
                              style={{padding:'4px 8px',borderRadius:6,border:'1px solid var(--border)',background:'var(--s2)',fontSize:11}}>
                              <option value="">— Select Rep —</option>
                              {reps.map(u=><option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                            </select>
                            <button className="btn btn-primary btn-sm" onClick={()=>doAssignRep(r)} style={{fontSize:10,padding:'4px 8px'}}>
                              <CheckCircle size={11}/> Save
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={()=>{setAssigningRep(null);setAssignRepId('');}} style={{fontSize:10,padding:'4px'}}>
                              ✕
                            </button>
                          </div>
                        ):(
                          <button className="btn btn-ghost btn-sm" onClick={()=>{setAssigningRep(r.id);setAssignRepId('');}}
                            style={{fontSize:11,color:'var(--primary)',gap:4}}>
                            <Users size={11}/> Assign Rep
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════
export default function ItemCommissionsPage(){
  const{hasPermission,isOwner}=useAuth();
  const canManageAll = isOwner || hasPermission('item_commissions.manage_all');
  const canManage    = canManageAll || hasPermission('item_commissions.manage');
  const canView      = canManage || hasPermission('item_commissions.view');
  const viewOwnOnly  = !canView && hasPermission('item_commissions.view_own');

  const[tab,setTab]=useState(canView||canManage?'master':'sales');
  const tabs=[
    ...(canView||canManage?[{id:'master',label:'Master Dataset',icon:Settings}]:[]),
    {id:'sales',label:'Sales Table',icon:List},
    {id:'reports',label:'Reports',icon:BarChart2},
  ];

  return(
    <div>
      <PageHeader title="Item Commission System" subtitle="Per-product net profit split — Business · Branch · Sales Rep"/>

      {/* Tab bar */}
      <div style={{display:'flex',gap:4,marginBottom:20,borderBottom:'2px solid var(--border)'}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{display:'flex',alignItems:'center',gap:7,padding:'10px 18px',fontSize:13,fontWeight:600,
              border:'none',borderBottom:`2px solid ${tab===t.id?'var(--primary)':'transparent'}`,
              background:'transparent',cursor:'pointer',color:tab===t.id?'var(--primary)':'var(--td)',
              marginBottom:-2,transition:'color .15s,border-color .15s'}}>
            <t.icon size={15}/>{t.label}
          </button>
        ))}
      </div>

      {tab==='master'  &&canManage&&<MasterDatasetTab canManage={canManage} canManageAll={canManageAll}/>}
      {tab==='sales'   &&<SalesTableTab canManage={canManage} viewOwnOnly={viewOwnOnly}/>}
      {tab==='reports' &&<CommissionReportsTab canManageAll={canManageAll} viewOwnOnly={viewOwnOnly}/>}
    </div>
  );
}
